import _ from "lodash";
import React, { PureComponent } from "react";
import styled from "styled-components";
import { compose } from "recompose";
import { AlertManager, withAlert } from "react-alert";
import FormSubmitButtons from "AuthorizationSharedComponents/FormSubmitButtons";
import RequiredFieldModal from "AuthorizationSharedComponents/RequiredFieldModal";

import {
  withPracticeSignatures,
  withSignatures,
  withDeleteSignature,
} from "../../../graphql/Signature";
import { withCurrentAccount } from "../../../graphql/Account";
import { withUpdateAuthorization } from "../../../graphql/Authorization";
import { isDisplayAuthEnrollment } from "../../../util/enrollUtils";
import { displayLetterOfMedicaNecessityForAuth } from "../../AuthorizationSharedSteps/LomnSection/LomnShared";
import {
  CommonSignatureBlock,
  NEW_TAB,
  SAVED_TAB,
  UPLOAD_TAB,
  allowFalsySignature,
} from "./CommonSignatureBlock";
import {
  Account,
  Authorization,
  AuthorizationForm,
  NewEnrollmentObjectType,
  File,
} from "@samacare/graphql";

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const PDFContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background-color: ${(props: any) => props.theme.lightGray};
`;

const HelperText = styled.div`
  font-size: 12px;
  color: ${(props: any) => props.theme.darkGray};
  margin-top: 10px;
  margin-bottom: 10px;
  font-weight: bold;
`;

const hideSignature = (
  form: AuthorizationForm,
  authorization: Authorization
): boolean => {
  if (authorization == null) return false;
  if (displayLetterOfMedicaNecessityForAuth(authorization)) return false;

  return (
    !_.get(form, "hasTaggedSignature") ||
    authorization.type ===
      window.CONFIG.CONSTANTS.AUTHORIZATION_TYPES.MULTI_PARTY.value
  );
};

interface ISignature {
  signatureId: string | null;
  signatureUrl: string | null;
}

interface SignatureProps {
  pdfPath?: string;
  back?: () => void;
  signatures: File[];
  disabled: boolean;
  authorization: Authorization;
  form: AuthorizationForm;
  account: Account;
  practiceSignatures?: File[];
  hideSignatureContainer?: boolean;
  updateSignature?: (id: string) => void;
  isSimplifiedUx?: boolean;
  nextStep: (data: ISignature) => void;
  addSignature?: (options: { variables: any }) => Promise<any>;
  deleteSignature?: (options: { variables: any }) => Promise<any>;
  alert: AlertManager;
  getSignatureId?: (data: any) => void;
  enrollment?: NewEnrollmentObjectType;
}

interface SignatureState {
  tab: string;
  selectedSignatureId: string | null;
  requiredFieldModalOpen: boolean;
  deleteSignatureId: string | null;
  deleteSignatureUrl: string;
  signatureDataUrl: string | null;
}

export class Signature extends PureComponent<SignatureProps, SignatureState> {
  isForEnrollment: boolean;
  mySignature: any;

  constructor(props: SignatureProps) {
    super(props);

    this.isForEnrollment = props.authorization == null;

    const signatureId: string = this.isForEnrollment
      ? props.enrollment?.SignatureFileId
      : props.authorization?.formDetails?.signatureId;

    this.mySignature = null;
    this.state = {
      tab: !signatureId ? NEW_TAB : SAVED_TAB,
      selectedSignatureId: signatureId,
      requiredFieldModalOpen: false,
      deleteSignatureId: null,
      deleteSignatureUrl: "",
      signatureDataUrl: "",
    };

    if (props.getSignatureId) {
      props.getSignatureId(this.saveAndGetSignatureData);
    }
  }

  componentWillUnmount() {
    const { getSignatureId } = this.props;

    if (getSignatureId) {
      getSignatureId(null);
    }
  }

  updateState = (newState: Partial<SignatureState>) => {
    this.setState(newState as SignatureState);
  };

  saveAndSetSignature = async (dataURL: string, addToInstitution = false) => {
    const { addSignature, alert, updateSignature } = this.props;

    try {
      const res = await addSignature!({
        variables: { data: dataURL, addToInstitution },
      });
      this.setState({
        selectedSignatureId: res.data.createSignature.id,
        tab: SAVED_TAB,
      });
      if (updateSignature) {
        updateSignature(res.data.createSignature.id);
      }
    } catch (e) {
      alert.error("There was an error setting your signature");
    }
  };

  saveAndGetSignatureData = async (): Promise<ISignature> => {
    const { selectedSignatureId, signatureDataUrl } = this.state;
    return {
      signatureId: selectedSignatureId,
      signatureUrl: signatureDataUrl,
    };
  };

  signAndSubmit = async () => {
    const { nextStep } = this.props;

    const { signatureId, signatureUrl } = await this.saveAndGetSignatureData();

    nextStep({ signatureId, signatureUrl });
  };

  triggerSubmit = () => {
    const { authorization, disabled, account, nextStep, form, isSimplifiedUx } =
      this.props;
    const { tab, selectedSignatureId, signatureDataUrl } = this.state;

    if (disabled) return;

    if (hideSignature(form, authorization)) {
      nextStep({ signatureId: null, signatureUrl: null });
    } else {
      const hasNoSignatureForOldUX =
        (tab === NEW_TAB && !selectedSignatureId) ||
        (tab === SAVED_TAB && !selectedSignatureId) ||
        tab === UPLOAD_TAB;
      const hasSignature = isSimplifiedUx
        ? Boolean(signatureDataUrl)
        : !hasNoSignatureForOldUX;
      if (!allowFalsySignature(account) && !hasSignature) {
        this.setState({ requiredFieldModalOpen: true });
        return;
      }

      void this.signAndSubmit();
    }
  };

  handleDeleteSignature = async () => {
    const { deleteSignatureId } = this.state;
    const { deleteSignature, alert } = this.props;
    this.setState({
      selectedSignatureId: null,
      deleteSignatureId: null,
      deleteSignatureUrl: "",
    });
    try {
      await deleteSignature!({ variables: { id: deleteSignatureId } });
      alert.info("Successfully deleted prescriber signature!");
    } catch {
      alert.error("Error deleting prescriber signature");
    }
  };

  render() {
    const {
      pdfPath,
      back,
      signatures,
      disabled,
      authorization,
      form,
      account,
      practiceSignatures,
      hideSignatureContainer,
      updateSignature,
      isSimplifiedUx,
    } = this.props;
    const {
      tab,
      selectedSignatureId,
      requiredFieldModalOpen,
      deleteSignatureId,
      deleteSignatureUrl,
    } = this.state;

    // This happens when one user saves a signature and another user goes to submit
    let otherUserSignatureId;
    if (
      selectedSignatureId &&
      !_.find(signatures, { id: selectedSignatureId })
    ) {
      otherUserSignatureId = selectedSignatureId;
    }
    const provider =
      authorization?.config?.PRESCRIBER_FIRST_NAME &&
      authorization?.config?.PRESCRIBER_LAST_NAME
        ? {
            firstName: authorization.config.PRESCRIBER_FIRST_NAME,
            lastName: authorization.config.PRESCRIBER_LAST_NAME,
          }
        : null;

    const isDisplayEnrollment = isDisplayAuthEnrollment(authorization);
    const submitText = isDisplayEnrollment
      ? ""
      : isSimplifiedUx && !hideSignatureContainer
      ? "Sign and Submit"
      : "Submit";

    return (
      <FormContainer>
        {!this.isForEnrollment && (
          <>
            <PDFContainer>
              <object
                data={pdfPath}
                height="700px"
                type="application/pdf"
                width="900px"
              >
                <embed
                  height="500px"
                  src={`${pdfPath}#view=FitH`}
                  type="application/pdf"
                  width="800px"
                />
              </object>
            </PDFContainer>
            <HelperText>
              Note: Our fax number will be entered into the form to allow
              receiving and processing responses on your behalf.
            </HelperText>

            {disabled ? null : !hideSignature(form, authorization) ? (
              <CommonSignatureBlock
                practiceSignatures={practiceSignatures}
                updateState={this.updateState}
                tab={tab}
                provider={provider}
                selectedSignatureId={selectedSignatureId}
                otherUserSignatureId={otherUserSignatureId}
                signatures={signatures}
                deleteSignatureId={deleteSignatureId}
                account={account}
                deleteSignatureUrl={deleteSignatureUrl}
                handleDeleteSignature={this.handleDeleteSignature}
                disabled={disabled}
                updateSignature={updateSignature}
                saveAndSetSignature={this.saveAndSetSignature}
                isForEnrollment={this.isForEnrollment}
              />
            ) : hideSignatureContainer ? null : (
              <div>This form does not require a signature</div>
            )}
            <FormSubmitButtons
              back={back}
              submit={disabled ? undefined : this.triggerSubmit}
              submitText={submitText}
            />
            <RequiredFieldModal
              closeModal={() => {
                this.setState({ requiredFieldModalOpen: false });
              }}
              open={requiredFieldModalOpen}
            />
          </>
        )}
        {this.isForEnrollment && (
          <CommonSignatureBlock
            practiceSignatures={practiceSignatures}
            updateState={this.updateState}
            tab={tab}
            provider={provider}
            selectedSignatureId={selectedSignatureId}
            otherUserSignatureId={otherUserSignatureId}
            signatures={signatures}
            deleteSignatureId={deleteSignatureId}
            account={account}
            deleteSignatureUrl={deleteSignatureUrl}
            handleDeleteSignature={this.handleDeleteSignature}
            disabled={disabled}
            saveAndSetSignature={this.saveAndSetSignature}
            updateSignature={updateSignature}
            isForEnrollment={this.isForEnrollment}
          />
        )}
      </FormContainer>
    );
  }
}

/**
 * @deprecated Use a functional component instead (non HOC)
 */
export default compose(
  withPracticeSignatures,
  withSignatures,
  withCurrentAccount,
  withDeleteSignature,
  withUpdateAuthorization
  // @ts-ignore
)(withAlert()(Signature));
